import { getPortalRoot } from '@src/components/portalRoot';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon } from '@src/components/icons/CheckIcon';
import { WarningIcon } from '@src/components/icons/WarningIcon';
import { classes } from '@src/components/layout/space';
import { testIdAttr } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Toast.css';

export type ToastTone = 'error' | 'success';

export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

type Listener = (item: ToastItem) => void;

let listener: Listener | null = null;
let nextId = 1;

/** Push a flash that ToastHost renders at the top of the viewport. */
export function showToast(message: string, tone: ToastTone = 'error'): void {
  listener?.({ id: nextId++, message, tone });
}

function subscribeToast(fn: Listener): () => void {
  listener = fn;
  return () => {
    if (listener === fn) {
      listener = null;
    }
  };
}

export interface ToastProps {
  children: ReactNode;
  tone?: ToastTone;
  testId?: string;
}

const HOLD_MS = 2500;

/**
 * A flash of copy. ToastHost slides one down from the top of the viewport.
 * Success is for something that left no trace on the screen — copying a link
 * to the clipboard. Anything the page can show in place should not be a toast.
 */
export function Toast({ children, tone = 'error', testId }: ToastProps) {
  return (
    <div
      className={classes('toast', tone === 'success' && 'toast--success')}
      role="alert"
      {...testIdAttr(testId)}
    >
      <span className="toast__icon" aria-hidden="true">
        {tone === 'success' ? <CheckIcon /> : <WarningIcon />}
      </span>
      <Typography as="p" role="body/sm" color="inherit">
        {children}
      </Typography>
    </div>
  );
}

/** Listens for showToast() and parks the flash above the sticky header. */
export function ToastHost() {
  const [item, setItem] = useState<ToastItem | null>(null);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => subscribeToast((next) => {
    setItem(next);
    setLeaving(false);
  }), []);

  useEffect(() => {
    if (!item || leaving) {
      return undefined;
    }
    const hide = window.setTimeout(() => setLeaving(true), HOLD_MS);
    return () => window.clearTimeout(hide);
  }, [item, leaving]);

  if (!item) {
    return null;
  }

  return createPortal(
    <div className="toast-host">
      <div
        className={classes('toast-host__item', leaving && 'toast-host__item--out')}
        onAnimationEnd={() => {
          if (leaving) {
            setItem(null);
          }
        }}
      >
        <Toast tone={item.tone} testId="toast">
          {item.message}
        </Toast>
      </div>
    </div>,
    getPortalRoot(),
  );
}
